// Thin wrapper around the trip-planner Worker API.
// Each function returns the unwrapped `data` field on success or throws an ApiError.

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/+$/, "");

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export class NetworkError extends Error {
  constructor(message) {
    super(message);
    this.code = "NETWORK";
  }
}

async function call(method, path, body) {
  if (!API_BASE) {
    throw new ApiError(0, "NO_API_BASE", "VITE_API_BASE is not configured");
  }
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new NetworkError(err?.message || "Network request failed");
  }
  let json;
  try { json = await res.json(); } catch {
    throw new ApiError(res.status, "BAD_RESPONSE", `Server returned non-JSON (${res.status})`);
  }
  if (!res.ok || !json?.ok) {
    const code = json?.error?.code || "UNKNOWN";
    const msg = json?.error?.message || `Request failed (${res.status})`;
    throw new ApiError(res.status, code, msg);
  }
  return json.data;
}

// ---------- Trips ----------

export const createTrip = ({ title, categories, legs }) =>
  call("POST", "/trips", { title, categories, legs });

export const fetchTrip = (tripId, legIds) => {
  const qs = new URLSearchParams({ legs: legIds.join(",") }).toString();
  return call("GET", `/trips/${tripId}?${qs}`);
};

export const duplicateTrip = (tripId, { title, categories, legs } = {}) =>
  call("POST", `/trips/${tripId}/duplicate`, { title, categories, legs });

// ---------- Legs ----------

export const addLeg = (tripId, leg) =>
  call("POST", `/trips/${tripId}/legs`, leg);

export const patchLeg = (legId, patch) =>
  call("PATCH", `/legs/${legId}`, patch);

export const deleteLeg = (legId) =>
  call("DELETE", `/legs/${legId}`);

// ---------- Items ----------

export const createItem = (legId, item) =>
  call("POST", `/legs/${legId}/items`, item);

export const patchItem = (itemId, patch) =>
  call("PATCH", `/items/${itemId}`, patch);

export const deleteItem = (itemId) =>
  call("DELETE", `/items/${itemId}`);
